import { matchServedDomain, normaliseHost } from './identityDomain';
/**
 * Impersonation Utility Functions
 * Handles impersonation API calls with loading states and error handling
 */

import { identityAuth } from '../auth/IdentityAuth';
import { toast } from 'react-toastify';
import { Product } from './Constants';

// Scope types for impersonation
export const ScopeType = {
  SCHOOL: 0,
  GROUP: 1,
};

// Callback URL mapping for different products.
//
// These name a single registrable domain. Impersonation hands the user to this URL, and the
// session cookie does not cross registrable domains — so from admin.edu-intelligence.ai a
// welbee.co.uk callback drops the impersonated session on arrival. getCallbackUrls() resolves
// them from the host in play; the constants below remain the build-time defaults.
export const CallbackUrls = {
  HOME: process.env.REACT_APP_HOME_CALLBACK_URL,
  WELLBEING: process.env.REACT_APP_WELLBEING_CALLBACK_URL,
  INSIGHTS: process.env.REACT_APP_INSIGHTS_CALLBACK_URL,
  EDU_INTELLIGENCE: process.env.REACT_APP_EDU_INTELLIGENCE_CALLBACK_URL,
  PULSE: process.env.REACT_APP_PULSE_CALLBACK_URL,
  EI_PRO:
    process.env.REACT_APP_EI_PRO_CALLBACK_URL ||
    process.env.REACT_APP_EDU_INTELLIGENCE_CALLBACK_URL,
  EI_ENTERPRISE:
    process.env.REACT_APP_EI_ENTERPRISE_CALLBACK_URL ||
    process.env.REACT_APP_EDU_INTELLIGENCE_CALLBACK_URL,
  SAFE_SPACE:
    process.env.REACT_APP_SAFE_SPACE_CALLBACK_URL ||
    process.env.REACT_APP_WELLBEING_CALLBACK_URL,
};

/**
 * Callback URLs for the domain this browser is actually on. Resolution is per key, so a product
 * with no host on that domain keeps its build-time value.
 */
export const getCallbackUrls = (
  host = typeof window === 'undefined' ? '' : window.location.hostname
) => {
  let byDomain = {};
  try {
    const raw = String(process.env.REACT_APP_CALLBACK_URLS_BY_DOMAIN || '').trim();
    const unquoted = raw.replace(/^(['"])([\s\S]*)\1$/, '$2');
    const parsed = unquoted ? JSON.parse(unquoted) : {};
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) byDomain = parsed;
  } catch {
    // fall back to the build-time values rather than breaking impersonation
  }

  const normalised = Object.fromEntries(
    Object.entries(byDomain).map(([k, v]) => [normaliseHost(k).replace(/^\./, ''), v])
  );
  const matched = matchServedDomain(host, Object.keys(normalised));
  const raw = (matched && normalised[matched]) || {};

  // Keep only usable values. A non-string or blank entry would otherwise become the callback and
  // send the user nowhere — falling back to the build-time value is the safe outcome.
  const overrides = Object.fromEntries(
    Object.entries(raw)
      .filter(([, v]) => typeof v === 'string' && v.trim())
      .map(([k, v]) => [k, v.trim()])
  );

  // Each key is: domain override, else its own build-time value, else the documented parent.
  // The parent must be the *resolved* one — deriving from CallbackUrls would leave EI_PRO and
  // friends on the build-time domain even after EDU_INTELLIGENCE was overridden.
  const pick = (key, ownValue, parent) => overrides[key] || ownValue || parent;

  const HOME = overrides.HOME || CallbackUrls.HOME;
  const WELLBEING = overrides.WELLBEING || CallbackUrls.WELLBEING;
  const INSIGHTS = overrides.INSIGHTS || CallbackUrls.INSIGHTS;
  const EDU_INTELLIGENCE = overrides.EDU_INTELLIGENCE || CallbackUrls.EDU_INTELLIGENCE;
  const PULSE = overrides.PULSE || CallbackUrls.PULSE;

  return {
    HOME,
    WELLBEING,
    INSIGHTS,
    EDU_INTELLIGENCE,
    PULSE,
    EI_PRO: pick('EI_PRO', process.env.REACT_APP_EI_PRO_CALLBACK_URL, EDU_INTELLIGENCE),
    EI_ENTERPRISE: pick(
      'EI_ENTERPRISE',
      process.env.REACT_APP_EI_ENTERPRISE_CALLBACK_URL,
      EDU_INTELLIGENCE
    ),
    SAFE_SPACE: pick('SAFE_SPACE', process.env.REACT_APP_SAFE_SPACE_CALLBACK_URL, WELLBEING),
  };
};

/** Product to callback URL mapping, resolved for the host in play. */
export const getProductCallbackMap = (host, urls) => {
  const u = urls || getCallbackUrls(host);
  return {
    [Product.MAIN_APP_TOOLKIT]: u.WELLBEING,
    [Product.STUDENT_INSIGHT]: u.INSIGHTS,
    [Product.PARENTS_INSIGHTS]: u.INSIGHTS,
    [Product.STAFF_INSIGHTS]: u.INSIGHTS,
    [Product.EDU_INTELLIGENCE]: u.EDU_INTELLIGENCE,
    [Product.PULSE]: u.PULSE,
    [Product.EI_PRO]: u.EI_PRO,
    [Product.EI_ENTERPRISE]: u.EI_ENTERPRISE,
    [Product.SAFE_SPACE_CHECK_IN]: u.SAFE_SPACE,
    // The newer EI enterprise tiers route to the Edu Intelligence app.
    [Product.EI_ENTERPRISE_TIER_2]: u.EI_ENTERPRISE,
    [Product.EI_ENTERPRISE_TIER_3]: u.EI_ENTERPRISE,
  };
};

/** Retained for callers that read the map directly; built from the build-time defaults. */
export const ProductCallbackMap = getProductCallbackMap('');

/**
 * Impersonate function with loading state management
 * @param {Object} params - Impersonation parameters
 * @param {number} params.entityType - ScopeType.SCHOOL or ScopeType.GROUP
 * @param {string|number} params.entityId - School ID or Group ID
 * @param {string} params.callbackUrl - URL to redirect to after successful impersonation
 * @param {Function} params.setLoading - Function to set loading state
 * @param {string} params.productName - Name of the product being accessed (for error messages)
 * @param {number} [params.productType] - Optional product type identifier sent to the API
 * @param {string} [params.email] - Optional email address for impersonation auditable context
 * @param {string} [params.entityName] - Display name of the school/group being entered. Identity
 *   stores no school/group records, so without this the audit trail can only record a numeric id
 *   and the log can't say WHICH customer was impersonated.
 * @returns {Promise<boolean>} - Success status
 */
export const impersonate = async ({
  entityType,
  entityId,
  callbackUrl,
  setLoading,
  productName = 'application',
  groupId = null,
  schoolIds = [],
  productType = null,
  email = null,
  entityName = null,
}) => {
  try {
    // Set loading state
    if (setLoading) {
      setLoading(true);
    }

    // Use numeric entity type expected by API (ScopeType.SCHOOL=0, ScopeType.GROUP=1)
    const entityTypeVal = entityType === ScopeType.GROUP ? ScopeType.GROUP : ScopeType.SCHOOL;

    // Build payload according to scenarios (numeric entityType)
    let accessTarget;
    let schoolAccess;

    if (entityTypeVal === ScopeType.GROUP) {
      // Case 1: Group impersonation (group-level). Impersonating a MAT means acting as the trust
      // across all its schools, so it always grants all-schools access — otherwise the session has
      // no group-wide reach and Access Management lists only users without a group-scope row. The
      // schoolIds are still forwarded for audit/linkage. This is the single place that guarantees it,
      // so an individual caller cannot regress it by omitting allSchools.
      accessTarget = { entityType: ScopeType.GROUP, entityId, groupId: null };
      schoolAccess = { schoolIds: schoolIds || [], allSchools: true };
    } else {
      // School impersonation
      // Case 2: From expanded group row (has groupId)
      // Case 3: From school list (no groupId)
      accessTarget = groupId != null
        ? { entityType: ScopeType.SCHOOL, entityId, groupId }
        : { entityType: ScopeType.SCHOOL, entityId };
      schoolAccess = { schoolIds: [], allSchools: false };
    }

    // Name the entity for the audit trail when the caller knows it (it is on screen at the point
    // of the click). Omitted rather than sent blank so Identity's own fallback label applies.
    const sanitizedEntityName = entityName == null ? '' : String(entityName).trim();
    if (sanitizedEntityName) {
      accessTarget.entityName = sanitizedEntityName;
    }

    const normalizedProductType =
      productType == null ? null : Number(productType);
    const sanitizedEmail =
      email == null ? '' : String(email).trim();
    const shouldIncludeEmail =
      normalizedProductType === Product.MAIN_APP_TOOLKIT &&
      sanitizedEmail.length > 0;

    const payload = {
      accessTarget,
      schoolAccess,
      callbackUrl,
      ...(normalizedProductType == null
        ? {}
        : { productType: normalizedProductType }),
      ...(shouldIncludeEmail ? { email: sanitizedEmail } : {}),
    };

    // Call the impersonate API
    const response = await identityAuth.impersonate(payload);

    if (response.ok) {
      // Success - redirect to callback URL
      toast.success(`Successfully accessing ${productName}`);
      window.open(callbackUrl, '_blank');
      return true;
    } else {
      // Handle API error
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || `Failed to access ${productName}`;
      toast.error(errorMessage);
      return false;
    }
  } catch (error) {
    // Handle network or other errors
    console.error('Impersonation error:', error);
    toast.error(`Failed to access ${productName}. Please try again.`);
    return false;
  } finally {
    // Clear loading state
    if (setLoading) {
      setLoading(false);
    }
  }
};

/**
 * Get callback URL for a specific product
 * @param {number} productId - Product ID (Product enum value)
 * @returns {string} - Callback URL
 */
export const getCallbackUrlForProduct = (productId, host) => {
  // Resolve once and pass it through — otherwise the domain map is parsed twice per call.
  const urls = getCallbackUrls(host);
  return getProductCallbackMap(host, urls)[productId] || urls.HOME;
};

/**
 * Get callback URL for home button
 * @param {number} entityType - ScopeType.SCHOOL or ScopeType.GROUP
 * @returns {string} - Home callback URL
 */
export const getHomeCallbackUrl = (entityType, host) => {
  return getCallbackUrls(host).HOME;
};
